import {
  type SccrsDepositFormValues,
  type SccrsOperationResponse,
  useSccrsFormLogic,
} from '@/entities/sccrs';
import { useCreateDepositOperation, useDepositCommission } from '@/shared/api';
import { useEffect, useMemo, useState } from 'react';
import { getDepositFiatCurrencyInputOptions } from '@/shared/lib';
import { useTranslation } from 'react-i18next';

interface UseDepositSccrsFormLogicOptions {
  onSubmit?: (data: SccrsDepositFormValues) => void;
  initialData?: Partial<SccrsDepositFormValues>;
}

export const useDepositSccrsFormLogic = ({
  onSubmit,
  initialData,
}: UseDepositSccrsFormLogicOptions) => {
  const { t } = useTranslation();

  const [showResultModal, setShowResultModal] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [currentMinAmount, setCurrentMinAmount] = useState(0);

  const {
    sccrsForm,
    watchedValues,
    platformOptions,
    isCommissionEnabled,
    hasCommissionInputs,
    debouncedAmount,
    userProfileInfoError,
    gamingHallsError,
    setFormData,
    accountNumber,
    normalizedAmount,
    isUserProfileInfoLoading,
    depositWithdrawAssetsData,
    assetsIconsData,
    currentCurrencyCode,
    isGamingHallsLoading,
  } = useSccrsFormLogic({
    initialData,
    isMinAmount: true,
    currentMinAmount,
  });

  // Currency options based on selected country (use actual form values)
  const currencyOptions = useMemo(() => {
    return getDepositFiatCurrencyInputOptions(
      'sccrs',
      depositWithdrawAssetsData?.value,
      assetsIconsData?.value
    );
  }, [depositWithdrawAssetsData, assetsIconsData]);

  const calculatedMinAmount = useMemo(() => {
    if (!watchedValues.currencyCode) {
      return 0;
    }

    const selectedOption = currencyOptions.find(
      o => o.value === watchedValues.currencyCode
    );

    return selectedOption?.min || 0;
  }, [watchedValues.currencyCode, currencyOptions]);

  useEffect(() => {
    setCurrentMinAmount(calculatedMinAmount);
  }, [calculatedMinAmount, watchedValues.currencyCode]);

  // Use current minAmount and currencyCode for display
  const minAmount = currentMinAmount;
  const amountPlaceholder = currentCurrencyCode
    ? t('common.amountMinAmountPlaceholder', {
        min: minAmount,
        currency: currentCurrencyCode,
      })
    : t('common.amountPlaceholder');

  const {
    mutate: createDeposit,
    isCreateOperationDepositLoading,
    createOperationDepositError,
  } = useCreateDepositOperation<SccrsOperationResponse>({
    operationType: 'sccrs',
    onSuccess: () => {
      setIsSuccess(true);
      setShowResultModal(true);
    },
    onError: () => {
      setIsSuccess(false);
      setShowResultModal(true);
    },
  });

  const {
    data: commissionData,
    isLoading: isCommissionFetching,
    error: commissionError,
  } = useDepositCommission({
    operationType: 'sccrs',
    params: {
      currencyCode: watchedValues.currencyCode,
      amount: debouncedAmount,
    },
    enabled: isCommissionEnabled,
  });

  const isAmountDebouncing =
    hasCommissionInputs && watchedValues.amount !== debouncedAmount;
  const isCommissionPending =
    hasCommissionInputs && (isAmountDebouncing || isCommissionFetching);
  const isCommissionData = commissionData && typeof commissionData === 'object';
  const shouldShowCommission =
    !Object.keys(sccrsForm.formState.errors).length &&
    hasCommissionInputs &&
    !isCommissionPending &&
    isCommissionData &&
    !commissionError;

  const isSubmitInProgressWithoutErrors =
    (isCreateOperationDepositLoading || isCommissionPending) &&
    !(commissionError || Object.keys(sccrsForm.formState.errors).length > 0);

  const handleFormSubmit = (data: SccrsDepositFormValues) => {
    if (
      Object.keys(sccrsForm.formState.errors).length > 0 ||
      Boolean(commissionError) ||
      isSubmitInProgressWithoutErrors ||
      !isCommissionData
    ) {
      return;
    }

    const body = {
      accountNumber,
      currencyCode: data.currencyCode,
      amount: data.amount,
      gamingHallId: data.platform,
    };

    setFormData(data);
    createDeposit(body);
    onSubmit?.(data);
  };

  const transformedCommissionData =
    isCommissionData &&
    commissionData &&
    typeof commissionData === 'object' &&
    'feeAmount' in commissionData
      ? {
          feeAmount: (commissionData as { feeAmount: number }).feeAmount,
          currencyCode: watchedValues.currencyCode,
          receiveAmount:
            Number(normalizedAmount) -
            (commissionData as { feeAmount: number }).feeAmount,
        }
      : undefined;

  const hasDataError =
    gamingHallsError || userProfileInfoError || createOperationDepositError;

  let firstError = null;

  if (createOperationDepositError) {
    firstError = createOperationDepositError;
  } else if (userProfileInfoError) {
    firstError = userProfileInfoError;
  } else if (gamingHallsError) {
    firstError = gamingHallsError;
  }

  return {
    form: sccrsForm,
    watchedValues,
    handleFormSubmit,

    showResultModal,
    isSuccess,

    // Data
    currencyOptions,
    platformOptions,
    minAmount,
    amountPlaceholder,

    // Commission
    commissionData: transformedCommissionData,
    isCommissionFetching,
    commissionError,
    shouldShowCommission,
    isCommissionPending,

    // States
    isSubmitInProgressWithoutErrors,
    formError: commissionError,

    // Loading states
    isLoading: isUserProfileInfoLoading || isGamingHallsLoading,
    isAccountInfoError: false,
    accountsInfoError: null,

    // Error states
    hasDataError,
    firstError,
  };
};
